import Phaser from 'phaser';
import type { MultiplayerHeroState, PlacementEntry } from '@roe/shared';
import { MultiplayerService } from '../services/MultiplayerService.js';

const PLACEMENT_DURATION = 20; // seconds
const BOARD_W = 960;
/** Minimum distance between two heroes' centres. Heroes are 40×40 sprites,
 *  so 48 leaves a small visible gap and prevents two heroes occupying the
 *  same hex. Used by the drag-end snap-back collision check. */
const SLOT_RADIUS = 48;

interface PlacementInitData {
  playerIndex: 1 | 2;
  heroes: MultiplayerHeroState[];
  /** Ally's heroes — rendered read-only so the local player can see where
   *  the partner is placing. Optional for back-compat (pre-fix callers
   *  pass nothing — overlay shows only own heroes, no ally render). */
  allyHeroes?: MultiplayerHeroState[];
  roomId: string;
}

export class PlacementOverlay extends Phaser.Scene {
  private playerIndex: 1 | 2 = 1;
  private heroes: MultiplayerHeroState[] = [];
  private allyHeroes: MultiplayerHeroState[] = [];
  private heroPositions = new Map<string, { x: number; y: number }>();
  /** Read-only ally positions (server-side coordinates, mirrored to local
   *  for player 2). Updated as the ally drags — used for collision detection
   *  so the local player can't drop on a slot the ally already claimed. */
  private allyPositions = new Map<string, { x: number; y: number }>();
  private timer = PLACEMENT_DURATION;
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private timerText!: ReturnType<typeof Phaser.Scene.prototype.add.text>;
  private confirmed = false;
  private mpService = MultiplayerService.getInstance();

  constructor() {
    super({ key: 'PlacementOverlay', active: false });
  }

  init(data: PlacementInitData): void {
    this.playerIndex = data.playerIndex;
    this.heroes = data.heroes;
    this.allyHeroes = data.allyHeroes ?? [];
    this.confirmed = false;
    this.timer = PLACEMENT_DURATION;
    this.heroPositions.clear();
    this.allyPositions.clear();

    // Initialize positions from current hero state
    for (const h of this.heroes) {
      this.heroPositions.set(h.heroId, { x: h.position.x, y: h.position.y });
    }
    // Ally positions — mirror x for player 2 perspective (server stores
    // canonical coords; local player 2 sees the right-half mirrored to left).
    for (const h of this.allyHeroes) {
      const localX = this.playerIndex === 2 ? BOARD_W - h.position.x : h.position.x;
      this.allyPositions.set(h.heroId, { x: localX, y: h.position.y });
    }
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Semi-transparent overlay background (left half only)
    this.add.rectangle(width / 4, height / 2, width / 2, height)
      .setFillStyle(0x000000, 0.5)
      .setDepth(10);

    // Timer display
    this.timerText = this.add.text(width / 4, 40, `Place heroes: ${this.timer}s`, {
      fontSize: '24px',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(11);

    // Ally hero sprites (read-only, greyed) — render BEFORE the local
    // heroes so dragging-in-progress sprites stack on top.
    for (const ally of this.allyHeroes) {
      const pos = this.allyPositions.get(ally.heroId) ?? ally.position;
      this.add.rectangle(pos.x, pos.y, 40, 40)
        .setFillStyle(0x666688) // grey-blue: ally
        .setStrokeStyle(1, 0x4444aa, 0.6)
        .setAlpha(0.55)
        .setDepth(11);
      this.add.text(pos.x, pos.y, ally.heroId, { fontSize: '10px', color: '#bbbbcc' })
        .setOrigin(0.5)
        .setDepth(12);
    }

    // Hero sprites (draggable rectangles)
    for (const hero of this.heroes) {
      const pos = this.heroPositions.get(hero.heroId) ?? hero.position;
      const sprite = this.add.rectangle(pos.x, pos.y, 40, 40)
        .setFillStyle(0x4488ff)
        .setInteractive({ draggable: true })
        .setDepth(12);
      // Snapshot the hero's pre-drag position so a colliding drop snaps
      // back to the last valid spot.
      let dragStartPos = { x: pos.x, y: pos.y };

      const label = this.add.text(pos.x, pos.y, hero.heroId, { fontSize: '10px', color: '#ffffff' })
        .setOrigin(0.5)
        .setDepth(13);

      sprite.on('dragstart', () => {
        const cur = this.heroPositions.get(hero.heroId);
        if (cur) dragStartPos = { x: cur.x, y: cur.y };
      });

      sprite.on('drag', (_ptr: unknown, x: number, y: number) => {
        // Constrain to left half
        const clampedX = Math.max(0, Math.min(BOARD_W / 2, x));
        // Visual update during drag — collision is enforced on dragend below.
        (sprite as unknown as { x: number; y: number }).x = clampedX;
        (sprite as unknown as { x: number; y: number }).y = y;
        label.x = clampedX;
        label.y = y;
      });

      sprite.on('dragend', () => {
        const sx = (sprite as unknown as { x: number; y: number }).x;
        const sy = (sprite as unknown as { x: number; y: number }).y;
        if (this._slotOccupied(hero.heroId, sx, sy)) {
          // Snap back — the target slot is occupied by self or ally.
          (sprite as unknown as { x: number; y: number }).x = dragStartPos.x;
          (sprite as unknown as { x: number; y: number }).y = dragStartPos.y;
          label.x = dragStartPos.x;
          label.y = dragStartPos.y;
          this.heroPositions.set(hero.heroId, { x: dragStartPos.x, y: dragStartPos.y });
          return;
        }
        this.heroPositions.set(hero.heroId, { x: sx, y: sy });
      });
    }

    // Confirm button
    this.add.rectangle(width / 4, height - 60, 160, 44)
      .setFillStyle(0x22aa44)
      .setInteractive()
      .setDepth(12)
      .on('pointerdown', () => this.confirmPlacement());

    this.add.text(width / 4, height - 60, 'Confirm', {
      fontSize: '18px',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(13);

    this._startTimer();

    // Listen for placement-phase-end (opponent submitted or timer expired server-side)
    this.mpService.onPlacementPhaseEnd(() => {
      this._stopTimer();
      this.scene.stop();
    });
  }

  confirmPlacement(): void {
    if (this.confirmed) return;
    this.confirmed = true;
    this._stopTimer();

    const placements: PlacementEntry[] = this.heroes.map(h => {
      const pos = this.heroPositions.get(h.heroId) ?? h.position;
      const serverPos = this.playerIndex === 2
        ? { x: BOARD_W - pos.x, y: pos.y }
        : { x: pos.x, y: pos.y };
      return { heroId: h.heroId, position: serverPos };
    });

    this.mpService.submitPlacement(placements);
    this.scene.stop();
  }

  private _startTimer(): void {
    this.timerInterval = setInterval(() => {
      this.timer--;
      if (this.timerText && typeof this.timerText.setText === 'function') {
        this.timerText.setText(`Place heroes: ${this.timer}s`);
      }
      if (this.timer <= 0) {
        this.confirmPlacement();
      }
    }, 1000);
  }

  private _stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /** Returns true if (x, y) is within SLOT_RADIUS of an existing hero
   *  (own or ally), excluding the hero currently being dragged. Used to
   *  reject drag-end drops that would put two heroes on the same hex. */
  private _slotOccupied(draggingHeroId: string, x: number, y: number): boolean {
    for (const [id, p] of this.heroPositions) {
      if (id === draggingHeroId) continue;
      if (Math.hypot(p.x - x, p.y - y) < SLOT_RADIUS) return true;
    }
    for (const [, p] of this.allyPositions) {
      if (Math.hypot(p.x - x, p.y - y) < SLOT_RADIUS) return true;
    }
    return false;
  }
}
